import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { CalendarDays, Plus, X, ChevronLeft, ChevronRight, Stethoscope, Users, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { workingDaysInRange, employeeNominalWorkday, isFrenchPublicHoliday, countEffectiveLeaveDays } from '../utils/workDays';
import { toLocalDateString } from '../services/api';

const LEAVE_TYPES = {
  conge: { field: 'specificLeaveDates', label: 'Congé', icon: '🏖️', badgeClass: 'bg-blue-100 text-blue-700' },
  maladie: { field: 'sickLeaveDates', label: 'Maladie', icon: '🤒', badgeClass: 'bg-red-100 text-red-700' },
  // Seul type à motif libre (RTT, absence injustifiée, événement familial...) :
  // stocké à part (otherAbsences: [{date, motif}]), pas une simple liste de dates.
  autre: { field: 'otherAbsences', label: 'Autre', icon: '📌', badgeClass: 'bg-purple-100 text-purple-700' },
};

// Formations (apprentis) : à part de LEAVE_TYPES — ce sont des PLAGES
// (trainingWeeks: [{start, end}]) gérées depuis la fiche salarié, pas des
// dates individuelles ajoutables/supprimables ici. Affichage seul dans le
// calendrier, pas de bouton de suppression.
const TRAINING_TYPE = { label: 'Formation', icon: '🎓', badgeClass: 'bg-yellow-100 text-yellow-700' };

// Jours NOMINAUX (planning habituel, hors fériés) d'une plage — contrairement
// à workingDaysInRange, ne regarde pas les absences du salarié : utilisé pour
// développer une semaine de formation, où la formation EST l'absence qu'on
// veut justement afficher (workingDaysInRange l'aurait exclue en la traitant
// comme un jour où « il ne travaille pas », donc jamais retournée).
const nominalDaysInRange = (employee, from, to) => {
  const days = [];
  const current = new Date(from);
  current.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (current <= end) {
    if (employeeNominalWorkday(employee, current)) days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const EMPTY_RANGE = { from: undefined, to: undefined };

const LeaveManager = ({ employees, onUpdateEmployee, onAddBulkAbsence }) => {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [showEmployeePicker, setShowEmployeePicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedType, setSelectedType] = useState('conge');
  const [otherMotif, setOtherMotif] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRange, setSelectedRange] = useState(EMPTY_RANGE);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const selectedEmployees = employees.filter(emp => selectedEmployeeIds.includes(emp.id));

  const toggleEmployee = (id) => {
    setSelectedEmployeeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const allSelected = employees.length > 0 && selectedEmployeeIds.length === employees.length;
  const toggleAll = () =>
    setSelectedEmployeeIds(allSelected ? [] : employees.map(emp => emp.id));

  // Jours ouvrés PROPRES À CHAQUE salarié (hors week-ends, jours fériés et
  // ses propres jours de repos) dans la plage sélectionnée : ce sont ces
  // jours-là qui comptent comme absence, pas les jours calendaires bruts.
  // Chacun peut donc recevoir un nombre de jours différent sur la même plage.
  const perEmployeeDays = selectedRange.from
    ? selectedEmployees.map(emp => ({
        employee: emp,
        days: workingDaysInRange(emp, selectedRange.from, selectedRange.to || selectedRange.from),
      }))
    : [];
  const totalDays = perEmployeeDays.reduce((sum, x) => sum + x.days.length, 0);

  const employeeLabel = selectedEmployeeIds.length === 0
    ? 'Sélectionner un ou plusieurs salariés'
    : selectedEmployeeIds.length === 1
      ? selectedEmployees[0]?.fullName
      : allSelected
        ? `Toute l'équipe (${selectedEmployeeIds.length})`
        : `${selectedEmployeeIds.length} salariés sélectionnés`;

  const handleAddLeaveRange = async () => {
    if (totalDays === 0) return;
    if (selectedType === 'autre' && !otherMotif.trim()) return;
    setIsSaving(true);
    try {
      // Un seul appel groupé : le serveur enregistre tous les salariés puis
      // réorganise le planning UNE fois (au lieu d'une fois par salarié).
      const assignments = {};
      perEmployeeDays.forEach(({ employee: emp, days }) => {
        if (days.length > 0) assignments[emp.id] = days.map(toLocalDateString);
      });
      await onAddBulkAbsence(selectedType, assignments, selectedType === 'autre' ? otherMotif.trim() : undefined);
      setShowCalendar(false);
      setSelectedRange(EMPTY_RANGE);
      setSelectedEmployeeIds([]);
      setOtherMotif('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveLeave = (employeeId, dateToRemove, type) => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee) return;

    const { field } = LEAVE_TYPES[type];
    // otherAbsences est une liste d'OBJETS {date, motif}, pas de dates brutes :
    // le filtre porte sur le champ date de chaque entrée.
    const updatedField = type === 'autre'
      ? (employee.otherAbsences || []).filter(
          item => new Date(item.date).getTime() !== new Date(dateToRemove).getTime()
        )
      : (employee[field] || []).filter(
          date => new Date(date).getTime() !== new Date(dateToRemove).getTime()
        );
    onUpdateEmployee({ ...employee, [field]: updatedField });
  };

  const getAllLeaves = () => {
    const allLeaves = [];
    employees.forEach(employee => {
      Object.entries(LEAVE_TYPES).forEach(([type, { field }]) => {
        if (type === 'autre') {
          (employee.otherAbsences || []).forEach(item => {
            allLeaves.push({ employee, date: new Date(item.date), type, motif: item.motif });
          });
          return;
        }
        (employee[field] || []).forEach(date => {
          allLeaves.push({
            employee,
            date: new Date(date),
            type,
          });
        });
      });
      // Formations : semaines converties en jours réellement travaillés par
      // l'apprenti (pas de formation affichée un jour de repos habituel).
      (employee.trainingWeeks || []).forEach(week => {
        if (!week.start || !week.end) return;
        nominalDaysInRange(employee, week.start, week.end).forEach(date => {
          allLeaves.push({ employee, date, type: 'formation' });
        });
      });
    });
    return allLeaves.sort((a, b) => a.date - b.date);
  };

  const allLeaves = getAllLeaves();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingCount = allLeaves.filter(leave => leave.date >= today).length;

  // --- Calendrier mensuel des absences ---
  const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7; // 0 = le mois commence un lundi
  const weeksToShow = Math.ceil((mondayOffset + daysInMonth) / 7);
  const calendarCells = Array.from({ length: weeksToShow * 7 }, (_, i) => {
    const cellDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1 - mondayOffset + i);
    return {
      date: cellDate,
      inMonth: cellDate.getMonth() === calendarMonth.getMonth(),
      isToday: cellDate.getTime() === today.getTime(),
      isHoliday: isFrenchPublicHoliday(cellDate),
      isWeekend: cellDate.getDay() === 0 || cellDate.getDay() === 6,
      leaves: allLeaves.filter(leave => leave.date.toDateString() === cellDate.toDateString()),
    };
  });

  const changeMonth = (delta) => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center text-blue-800">
          <CalendarDays className="w-5 h-5 mr-2" />
          Congés et Maladie
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Ajout de congé ou arrêt maladie */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-3">Ajouter une absence</h3>
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <Popover open={showEmployeePicker} onOpenChange={setShowEmployeePicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between border-blue-200 font-normal text-left"
                  >
                    <span className="flex items-center truncate">
                      <Users className="w-4 h-4 mr-2 shrink-0 text-blue-500" />
                      <span className={selectedEmployeeIds.length ? 'text-blue-900' : 'text-muted-foreground'}>
                        {employeeLabel}
                      </span>
                    </span>
                    <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <label
                    htmlFor="leave-emp-all"
                    className="flex items-center space-x-2 px-3 py-2 border-b border-blue-100 cursor-pointer hover:bg-blue-50"
                  >
                    <Checkbox id="leave-emp-all" checked={allSelected} onCheckedChange={toggleAll} />
                    <span className="text-sm font-medium text-blue-800">Toute l'équipe</span>
                  </label>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {employees.map(emp => (
                      <label
                        key={emp.id}
                        htmlFor={`leave-emp-${emp.id}`}
                        className="flex items-center space-x-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50"
                      >
                        <Checkbox
                          id={`leave-emp-${emp.id}`}
                          checked={selectedEmployeeIds.includes(emp.id)}
                          onCheckedChange={() => toggleEmployee(emp.id)}
                        />
                        <span className="text-sm text-blue-900 flex-1">{emp.fullName}</span>
                        {emp.active === false && (
                          <span className="text-xs text-gray-400">inactif</span>
                        )}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="border-blue-200 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LEAVE_TYPES).map(([type, { label, icon }]) => (
                  <SelectItem key={type} value={type}>
                    {icon} {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover
              open={showCalendar}
              onOpenChange={(open) => {
                setShowCalendar(open);
                if (!open) setSelectedRange(EMPTY_RANGE);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={selectedEmployeeIds.length === 0}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Date(s)
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={selectedRange}
                  onSelect={(range) => setSelectedRange(range || EMPTY_RANGE)}
                  locale={fr}
                />
                <div className="p-3 border-t border-blue-100 space-y-2">
                  <p className="text-xs text-blue-600">
                    {selectedRange.from
                      ? `${totalDays} jour(s) ouvré(s) au total${!selectedRange.to ? ' (clique une 2e date pour une plage)' : ''}`
                      : 'Clique une date de début, puis une date de fin pour une plage'}
                  </p>
                  {selectedRange.from && perEmployeeDays.length > 1 && (
                    <div className="text-xs text-blue-700 space-y-0.5 max-h-28 overflow-y-auto">
                      {perEmployeeDays.map(({ employee: emp, days }) => (
                        <div key={emp.id} className="flex justify-between">
                          <span className="truncate mr-2">{emp.fullName}</span>
                          <span className={days.length === 0 ? 'text-gray-400' : ''}>
                            {days.length} j.
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedType === 'autre' && (
                    <Input
                      value={otherMotif}
                      onChange={(e) => setOtherMotif(e.target.value)}
                      placeholder="Motif (obligatoire)"
                      className="border-blue-200 text-xs h-8"
                    />
                  )}
                  <Button
                    size="sm"
                    onClick={handleAddLeaveRange}
                    disabled={totalDays === 0 || isSaving || (selectedType === 'autre' && !otherMotif.trim())}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isSaving
                      ? 'Enregistrement...'
                      : `Ajouter${totalDays > 0 ? ` (${totalDays} j.)` : ''}`}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Calendrier des absences */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-blue-800 flex items-center">
              <CalendarDays className="w-4 h-4 mr-1" />
              Calendrier des absences ({upcomingCount} à venir)
            </h3>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeMonth(-1)}
                className="h-7 w-7 p-0 text-blue-700 hover:bg-blue-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium text-blue-800 w-36 text-center capitalize">
                {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeMonth(1)}
                className="h-7 w-7 p-0 text-blue-700 hover:bg-blue-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px bg-blue-100 rounded-lg overflow-hidden border border-blue-100">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(dayLabel => (
              <div key={dayLabel} className="bg-blue-50 text-center text-xs font-medium text-blue-800 py-1">
                {dayLabel}
              </div>
            ))}
            {calendarCells.map((cell) => (
              <div
                key={cell.date.toISOString()}
                className={`min-h-16 p-1 text-xs ${
                  !cell.inMonth
                    ? 'bg-gray-50 text-gray-300'
                    : cell.isHoliday || cell.isWeekend
                      ? 'bg-gray-100'
                      : 'bg-white'
                } ${cell.isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
              >
                <div className={`flex items-center justify-between ${cell.inMonth ? 'text-blue-900' : ''}`}>
                  <span className={`font-medium ${cell.isToday ? 'text-blue-700' : ''}`}>{cell.date.getDate()}</span>
                  {cell.isHoliday && cell.inMonth && <span title="Jour férié">🎌</span>}
                </div>
                {cell.inMonth && cell.leaves.length > 0 && (
                  <div className="mt-0.5 space-y-0.5">
                    {cell.leaves.map((leave, i) => {
                      const isTraining = leave.type === 'formation';
                      const typeInfo = isTraining ? TRAINING_TYPE : LEAVE_TYPES[leave.type];
                      const key = `${leave.employee.id}-${leave.type}-${i}`;
                      return (
                        <div
                          key={key}
                          className={`group flex items-center justify-between rounded px-1 py-px ${typeInfo.badgeClass}`}
                          title={`${leave.employee.fullName} — ${typeInfo.label}${leave.motif ? ' : ' + leave.motif : ''}`}
                        >
                          <span className="truncate">
                            {typeInfo.icon} {leave.employee.fullName.split(' ')[0]}
                          </span>
                          {!isTraining && (
                            <button
                              onClick={() => handleRemoveLeave(leave.employee.id, leave.date, leave.type)}
                              className="hidden group-hover:block text-red-600 hover:text-red-800 ml-0.5 shrink-0"
                              title="Supprimer cette absence"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Statistiques par employé */}
        <div>
          <h3 className="text-sm font-medium text-blue-800 mb-3">Absences par salarié</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {employees.map(employee => {
              // Seuls les jours théoriquement travaillés comptent : un congé posé
              // un week-end ou un férié ne consomme pas de jour de congé.
              const congeCount = countEffectiveLeaveDays(employee, employee.specificLeaveDates);
              const maladieCount = countEffectiveLeaveDays(employee, employee.sickLeaveDates);
              const autreCount = countEffectiveLeaveDays(employee, (employee.otherAbsences || []).map(a => a.date));
              const formationCount = (employee.trainingWeeks || []).reduce(
                (sum, week) => sum + (week.start && week.end ? nominalDaysInRange(employee, week.start, week.end).length : 0), 0
              );
              const remainingLeaves = employee.annualLeave - congeCount;

              return (
                <div key={employee.id} className="bg-white p-3 rounded border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-blue-800">{employee.fullName}</span>
                    <div className="flex items-center space-x-1">
                      {employee.isQualified && <Badge variant="outline" className="text-xs">Qualifié</Badge>}
                      {employee.isApprentice && <Badge variant="outline" className="text-xs text-yellow-600">Apprenti</Badge>}
                    </div>
                  </div>
                  <div className="text-xs text-blue-600 space-y-1">
                    <div className="flex justify-between">
                      <span>🏖️ Congés pris/programmés:</span>
                      <span className="font-medium">{congeCount} j.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center"><Stethoscope className="w-3 h-3 mr-1" />Arrêts maladie:</span>
                      <span className="font-medium">{maladieCount} j.</span>
                    </div>
                    {autreCount > 0 && (
                      <div className="flex justify-between">
                        <span>📌 Autres absences:</span>
                        <span className="font-medium">{autreCount} j.</span>
                      </div>
                    )}
                    {employee.isApprentice && (
                      <div className="flex justify-between">
                        <span>🎓 Formations:</span>
                        <span className="font-medium">{formationCount} j.</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Restants (congés):</span>
                      <span className={`font-medium ${remainingLeaves < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {remainingLeaves} j.
                      </span>
                    </div>
                  </div>
                  {remainingLeaves < 0 && (
                    <p className="text-xs text-red-600 mt-2">⚠️ Dépassement des congés annuels</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaveManager;
